// src/pages/students/details/DocumentsTab.jsx
import { useEffect, useState } from 'react';
import Card from '../../../components/common/Card';
import Button from '../../../components/common/Button';
import { useStudentDocuments } from '../../../hooks/useStudentDocuments';
import { PaperClipIcon, EyeIcon, TrashIcon } from '@heroicons/react/24/outline';

const DOC_TYPE_OPTIONS = [
  { value: 'university_certificate', label: 'University Certificate' },
  { value: 'id_document', label: 'ID / Passport' },
  { value: 'payment_receipt', label: 'Payment Receipt' },
  { value: 'other', label: 'Other' },
];

export default function DocumentsTab({ studentId }) {
  const {
    items: documents,
    loading: docsLoading,
    error: docsError,
    list: listDocuments,
    upload: uploadDocument,
    getSignedUrl,
    remove: removeDocument,
  } = useStudentDocuments();

  // Upload form state
  const [docFile, setDocFile] = useState(null);
  const [docType, setDocType] = useState('university_certificate');
  const [docNotes, setDocNotes] = useState('');
  const [uploading, setUploading] = useState(false);

  // initial + tab revisit fetch
  useEffect(() => {
    if (studentId) listDocuments(studentId);
  }, [studentId, listDocuments]);

  const onUpload = async (e) => {
    e.preventDefault();
    if (!docFile) return alert('Please choose a file.');
    if (docFile.size > 5 * 1024 * 1024) return alert('File is too large. Maximum size: 5 MB.');

    // simple mime check (optional)
    const okType = ['application/pdf', 'image/png', 'image/jpeg'].includes(docFile.type);
    if (!okType) return alert('Only PDF, PNG or JPEG files are allowed.');

    setUploading(true);
    try {
      const res = await uploadDocument(studentId, docFile, { docType, notes: docNotes });
      if (!res?.success) alert(res?.error || 'Upload failed');
      else {
        setDocFile(null);
        setDocNotes('');
        await listDocuments(studentId);
      }
    } catch (err) {
      console.error(err);
      alert('Unexpected error during upload.');
    } finally {
      setUploading(false);
    }
  };

  const openSigned = async (storagePath) => {
    const { success, url, error } = await getSignedUrl(storagePath, 120);
    if (!success) return alert(error || 'Could not create signed URL');
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const onRemoveDoc = async (doc) => {
    const ok = window.confirm('Remove this document? It will be soft-deleted.');
    if (!ok) return;
    const res = await removeDocument(doc.id, { hardDelete: false });
    if (!res?.success) alert(res?.error || 'Failed to remove document');
    else await listDocuments(studentId);
  };

  return (
    <Card>
      <div className="space-y-6">
        {/* Upload bar */}
        <form onSubmit={onUpload} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Document File (PDF/PNG/JPEG)</label>
            <input
              type="file"
              accept="application/pdf,image/png,image/jpeg"
              onChange={(e) => setDocFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-gray-900 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border file:border-gray-300 file:text-sm file:font-medium file:bg-white hover:file:bg-gray-50"
            />
            <p className="mt-1 text-xs text-gray-500">Max file size: 5 MB</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {DOC_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <input
              type="text"
              value={docNotes}
              onChange={(e) => setDocNotes(e.target.value)}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex">
            <Button type="submit" disabled={uploading} className="ml-auto flex items-center">
              <PaperClipIcon className="h-4 w-4 mr-1" />
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
          </div>
        </form>

        {/* List */}
        {docsLoading ? (
          <div className="py-8 text-center text-gray-500">Loading documents…</div>
        ) : docsError ? (
          <div className="py-8 text-center text-red-600">Error: {docsError}</div>
        ) : documents.length === 0 ? (
          <div className="py-12 text-center text-gray-500">No documents uploaded yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {documents.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {DOC_TYPE_OPTIONS.find((x) => x.value === doc.doc_type)?.label || doc.doc_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">{doc.filename}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {doc.size_bytes ? `${(doc.size_bytes / (1024 * 1024)).toFixed(2)} MB` : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {doc.created_at ? new Date(doc.created_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{doc.notes || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => openSigned(doc.storage_path)}
                          className="inline-flex items-center text-blue-600 hover:text-blue-800"
                          title="View / Download"
                        >
                          <EyeIcon className="h-4 w-4 mr-1" />
                          View
                        </button>
                        <button
                          onClick={() => onRemoveDoc(doc)}
                          className="inline-flex items-center text-red-600 hover:text-red-800"
                          title="Remove"
                        >
                          <TrashIcon className="h-4 w-4 mr-1" />
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}
